import React, { useState, useEffect, useCallback } from 'react';
import { Avatar, Box, Button, Container, IconButton, InputBase, LinearProgress, linearProgressClasses, Pagination, Paper, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tooltip, Typography, CircularProgress, Drawer, Backdrop, Link, Checkbox, FormControlLabel, DialogContent, Dialog, Alert } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import AddWorkspace from '../modals/workspaces/AddWorkspace';
import Grid from '@mui/material/Grid2';
import DeletePopup from '../modals/DeletePopup';
import AuthService from '../services/auth.service';
import EditWorkspace from '../modals/workspaces/EditWorkspace';
import { decodeMetadataToken } from '../utils/token.utils';
import { DeleteIconBlue, ModalClose, PasswordIconBlue } from '../assets/icons';
import ChangePasswordModal from '../modals/ChangePassword';
import BuyAddOns from '../modals/BuyAddOns';
import { formatDate, formatPlanName } from '../utils/common.util';
import PricingSection from './subscription/pricing/PricingSection';
import ChargebeeService from '../services/chargebee.service';
import { toast } from 'react-toastify';
import CancelAccountModal from '../modals/CancelAccount';
import PricingSectionV2 from './subscription/pricing/PricingSectionV2';
import Loader from './common/Loader';
import MixpanelService from '../services/mixpanel.service';


interface CompanyData {
    userId: string;
    companyId: string;
    companyName: string;
    domains: {
        easycrm: string;
        easywebinar: string;
    }
    customDomain: string;
    email: string;
    firstName: string;
    lastName: string;
    name: string;
    parentCompanyId: string;
    jobTitle?: string;
    phoneNumber: string;
    picture?: string;
    username?: string;
    timezone?: string;
    subscription?: {
        id: string
        items?: { itemPriceId: string, quantity: number, itemType: string, amount: number }[];
        status?: string;
        trialStartDate?: number;
        nextBillingAt?: number;
    };
    usage?: {
        event_id: string;
        event_count: number;
        max_count: number;
        automated_attendees_count:number;
    }[];
}

// Move styled components outside
const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 10,
    borderRadius: 4,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: "#DDE0E5",
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 16,
        backgroundColor: '#178EFC',
    },
}));

const Search = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(3),
        width: 'auto',
    },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1, 1, 0),
        paddingLeft: `calc(1em + ${theme.spacing(4)})`,
        transition: theme.transitions.create('width'),
        width: '100%',
        [theme.breakpoints.up('md')]: {
            width: '100%',
            border: '1px solid #E8EAF0',
            borderRadius: '8px',
        },
    },
}));

const calculateUsagePercentage = (usage: CompanyData['usage'] | undefined, eventId: string): number => {
    if (!usage) return 0;
    
    const event = usage.find(u => u.event_id === eventId);
    if (!event || event.max_count <= 0) return 0;
    
    return (event.event_count / event.max_count) * 100;
};

const getEventValue = (
  usage: CompanyData['usage'] | undefined,
  eventId: string,
  field: 'event_count' | 'max_count' | 'automated_attendees_count',
  company: any = ""
): number => {
  if (!usage) return 0;

  const event = usage.find(u => u.event_id === eventId);
  if (!event) return 0;

  // Special logic for All-In-One plan and max_count
  if (
    field === "max_count" &&
    typeof company === "string" &&
    company.includes("All-In-One") &&
    (eventId === 'attendee_joined' || eventId === 'attendee_registered')
  ) {
    const value = event[field];
    // Mapping for All-In-One plan max counts
    const mapping: Record<number, [number, number]> = {
      250: [50, 200],
      750: [250, 500],
      3500: [500, 3000],
      7000: [1000, 6000],
      12000: [2000, 10000],
    };
    if (value >= 12000) {
      return eventId === 'attendee_joined' ? 2000 : 10000;
    }
    if (mapping[value]) {
      return eventId === 'attendee_joined' ? mapping[value][0] : mapping[value][1];
    }
    return event[field];
  }

  return event[field];
};

const Workspaces = () => {
    const [dialogs, setDialogs] = useState({
        add: false,
        edit: false,
        delete: false
    });

    const [companies, setCompanies] = useState<CompanyData[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedWorkspace, setSelectedWorkspace] = useState<CompanyData | null>(null);
    const [page, setPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const ITEMS_PER_PAGE = 5;

    const [changePasswordDialog, setChangePasswordDialog] = useState(false);

    const [dialogBuyAddOns, setDialogBuyAddOns] = useState(false);
    const [dialogChangePlan, setDialogChangePlan] = useState(false);
    const [showPricing, setShowPricing] = useState(false);

    const [isChangingPlan, setIsChangingPlan] = useState(false);

    // Add new state for cancel account dialog
    const [dialogCancelAccount, setDialogCancelAccount] = useState(false);
    const [dialogCancelAddOn, setDialogCancelAddOn] = useState(false);
    const [selectedAddOnId, setSelectedAddOnId] = useState<string | null>(null);

    // Add loading state for delete operation
    const [isDeleting, setIsDeleting] = useState(false);

    const [isFreeTrial, setIsFreeTrial] = useState(false);
    const [isPausedPlan, setIsPausedPlan] = useState(false);

    const fetchCompanies = useCallback(async () => {
        try {
            setLoading(true);
            const metadata = decodeMetadataToken()

            
            // Check if user is on free trial plan
            const isUserOnFreeTrial = metadata?.companies?.[0]?.subscription?.status === 'in_trial' || 
                                     (metadata?.companies?.[0]?.subscription && 
                                      metadata.companies[0].subscription.planId?.includes('free-trial-plan'));
            
            setIsFreeTrial(isUserOnFreeTrial);

            // Check if user is on paused plan
            const isUserOnPausedPlan =
                metadata?.companies?.[0]?.subscription?.status === 'paused' ||
                metadata?.companies?.[0]?.subscription?.planId?.toLowerCase()?.includes('subscription-pause');
            
            setIsPausedPlan(isUserOnPausedPlan);
            
            // Only fetch companies if not on free trial and not on paused plan
            if (!isUserOnFreeTrial && !isUserOnPausedPlan) {
                const companyId = metadata?.companies?.[0]?.id
                const parentCompanyId = companyId;
                const response = await AuthService.getCompaniesByParentId(parentCompanyId);
                setCompanies(response.data);
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCompanies();
    }, [fetchCompanies]);

    // Filter companies based on search query
    const filteredCompanies = companies.filter(company =>
        company.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        company.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        company.email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Update pagination to use filtered companies
    const totalPages = Math.ceil(filteredCompanies.length / ITEMS_PER_PAGE);
    const paginatedCompanies = filteredCompanies.slice(
        (page - 1) * ITEMS_PER_PAGE,
        page * ITEMS_PER_PAGE
    );

    // Reset page when search query changes
    useEffect(() => {
        setPage(1);
    }, [searchQuery]);

    const handleWorkspaceAction = async (action: 'add' | 'edit' | 'delete') => {
        setDialogs(prev => ({ ...prev, [action]: false }));
        setSelectedWorkspace(null);
        await fetchCompanies();
    };

    const handleChangePagination = (event: any, value: any) => {
        setPage(value);
    };

    const handleAddWorkplaceDialogOpen = () => {
        setDialogs(prev => ({ ...prev, add: true }));
    };

    const handleAddWorkplaceDialogClose = () => {
        setDialogs(prev => ({ ...prev, add: false }));
    };

    const handleEditWorkplaceDialogOpen = (company: CompanyData) => {
        setSelectedWorkspace(company);
        setDialogs(prev => ({ ...prev, edit: true }));
    };

    const handleEditWorkplaceClose = () => {
        setDialogs(prev => ({ ...prev, edit: false }));
    };

    const handleCancelClose = () => {
        setDialogs(prev => ({ ...prev, delete: false }));
    };

    const [open, setOpen] = useState(false);

    const toggleDrawer = (status: boolean, company?: CompanyData) => {
        setOpen(status);

        if (status && company) {
            setSelectedWorkspace(company);
        } else {
            setSelectedWorkspace(null);
        }
    };

    // tabs
    const [tabValue, setTabValue] = useState<number>(0);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleDialogBuyAddOnsConfirm = async () => {
        handleDialogBuyAddOnsClose();   
        toggleDrawer(false);
        await fetchCompanies();
    };

    const handleChangePasswordConfirm = () => {
        setChangePasswordDialog(false);
    };

    const handleDialogBuyAddOns = () => {
        setDialogBuyAddOns(true);
    };

    const handleDialogBuyAddOnsClose = () => {
        setDialogBuyAddOns(false);
    };

    const handleDialogChangePlan = () => {
        setShowPricing(true);
    };

    const handleDialogChangePlanClose = () => {
        setDialogChangePlan(false);
    };

    const handlePlanChanged = async (planId: string) => {
        const subscriptionId = selectedWorkspace?.subscription?.id;
        if (!subscriptionId) {
            console.error("No subscription ID found");
            return;
        }

        const basePlanId = planId.split('-').slice(0, -1).join('-');
        const units = parseInt(planId.split('-').pop() || '0');

        setIsChangingPlan(true);
        try {
            await ChargebeeService.changePlan(subscriptionId, basePlanId, units);
            setShowPricing(false);
            toggleDrawer(false);
            await fetchCompanies();
            toast.success("Plan changed successfully");
        } catch (error) {
            console.error('Error changing plan:', error);
            toast.error("Error changing plan");
        } finally {
            setIsChangingPlan(false);
        }
    };

    // Add handler for cancel account dialog
    const handleDialogCancelAccount = () => {
        setDialogCancelAccount(true);
    };

    const handleCancelAddOn = (itemPriceId: string) => {
        setSelectedAddOnId(itemPriceId);
        setDialogCancelAddOn(true);
    }

    const handleCancelAddOnClose = () => {
        setDialogCancelAddOn(false);
        setSelectedAddOnId(null);
    };

    const handleCancelAddOnConfirm = async () => {
        try {
            const subscriptionId = selectedWorkspace?.subscription?.id;
            
            if (!subscriptionId) {
              console.error("No subscription ID available");
              return;
            }
            
            // Get Chargebee instance
            const chargebeeInstance = (window as any).Chargebee?.getInstance();
            if (!chargebeeInstance) {
                throw new Error("Chargebee not initialized");
            }
            
            const cbPortal = chargebeeInstance.createChargebeePortal();
            const callbacks = {
                close: async () => {
                    console.log('Edit subscription section closed');
                    // refetch companies data
                    try {
                        toggleDrawer(false);
                        await fetchCompanies();
                    } catch (refreshError) {
                        console.error('Failed to refresh after subscription edit:', refreshError);
                    }
                },
                loaded: () => {
                    console.log('Edit subscription section loaded');
                },
                error: (error: any) => {
                    console.error('Portal error:', error);
                }
            };
            
            cbPortal.open(callbacks, {
                sectionType: (window as any).Chargebee.getPortalSections().EDIT_SUBSCRIPTION,
                params: {
                    subscriptionId: subscriptionId
                }
            });
            
        } catch (error) {
            console.error("Error cancelling addon:", error);
            toast.error("An error occurred while cancelling the add-on");
        } finally {
            setDialogCancelAddOn(false);
        }
    };

    const handleDialogCancelAccountClose = () => {
        setDialogCancelAccount(false);
    };

    // Handle delete workspace
    const handleDeleteWorkspace = async () => {
        if (!selectedWorkspace) {
            return;
        }
        
        try {
            setIsDeleting(true);
            await AuthService.deleteWorkspace(selectedWorkspace.companyId);

            // Show success message
            toast.success("Workspace deleted successfully");
            
            // Close the dialog and refresh companies list
            setDialogs(prev => ({ ...prev, delete: false }));
            setSelectedWorkspace(null);
            setOpen(false);
            await fetchCompanies();
            
        } catch (error: any) {
            toast.error(error.message || "Failed to delete workspace");
        } finally {
            setIsDeleting(false);
        }
    };

    // Open delete dialog with the selected workspace
    const handleDeleteDialogOpen = (company: CompanyData | null) => {
        if (company) {
            setSelectedWorkspace(company);
            setDialogs(prev => ({ ...prev, delete: true }));
        }
    };


    return (
        <>
            <Container sx={{ maxWidth: "1400px !important", px: "0 !Important" }}>
                <Box mb="24px">
                    <Box mb="24px">
                        <Typography color="#333333" fontSize="24px" mb="15px" fontWeight="500">
                            Sub Accounts
                        </Typography>
                        <Typography>Manage your sub accounts with add-on accounts</Typography>
                    </Box>
                    
                    {(isFreeTrial || isPausedPlan) ? (
                        <Box mb={4}>
                            <Alert 
                                severity="info"
                                sx={{
                                    borderRadius: '8px',
                                    boxShadow: '0px 4px 12px 0px #2B2B300F',
                                    p: 3,
                                    backgroundColor: isPausedPlan ? '#FFF8E6' : '#E9F4FF',
                                    color: '#333333',
                                    border: isPausedPlan ? '1px solid #FFE082' : undefined,
                                    '& .MuiAlert-icon': {
                                        color: isPausedPlan ? '#F9A825' : '#178EFC'
                                    }
                                }}
                            >
                                <Typography variant="h6" mb={1} fontWeight="500">
                                    {isPausedPlan
                                        ? 'Client Workspaces Unavailable'
                                        : 'Upgrade Your Plan to Access Workspaces'}
                                </Typography>
                                <Typography mb={2}>
                                    {isPausedPlan
                                        ? 'The Client Workspaces feature is not available while your subscription is paused. Resume your subscription to access this feature and manage multiple client accounts.'
                                        : 'The Client Workspaces feature is not available on the Free Trial Plan. Upgrade to a paid plan to unlock this feature and manage multiple client accounts.'}
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disableElevation
                                    href={isPausedPlan ? '/dashboard/subscription' : '/dashboard/subscription'}
                                    sx={{
                                        textTransform: "capitalize",
                                        px: "24px",
                                        height: "40px",
                                        fontSize: "16px",
                                        borderRadius: "8px",
                                        minWidth: "fit-content",
                                        "&:hover": {
                                            border: "none !important",
                                            bgcolor: "#066fcffa",
                                        }
                                    }}
                                >
                                    {isPausedPlan ? 'Resume Subscription' : 'Upgrade Plan'}
                                </Button>
                            </Alert>
                        </Box>
                    ) : (
                        <>
                            <Box mb="12px" display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                                <Box maxWidth={273} width="100%">
                                    <Search sx={{ background: "#FFFFFF", "&:hover": { background: "#FFFFFF" }, ml: "auto !important", minWidth: "150px", width: "100%", mr: 0 }}>
                                        <SearchIconWrapper>
                                            <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/search-normal.svg" alt="search-icon" />
                                        </SearchIconWrapper>
                                        <StyledInputBase
                                            placeholder="Search…"
                                            inputProps={{ 'aria-label': 'search' }}
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                    </Search>
                                </Box>
                                <Box display='flex' flexWrap="wrap" gap="12px">
                                    
                                    <Button
                                        onClick={
                                            () => {
                                                MixpanelService.track("ua_subaccount_add_clicked", {
                                                    "Click 'Add Sub Account' button": "screen: 'workspaces'",
                                                    "total_sub_accounts": companies.length,
                                                });
                                                handleAddWorkplaceDialogOpen();
                                            }
                                        }
                                        variant="contained"
                                        color="primary"
                                        disableElevation
                                        sx={{
                                            textTransform: "capitalize",
                                            px: "24px",
                                            height: "40px",
                                            fontSize: "16px",
                                            borderRadius: "8px",
                                            minWidth: "fit-content",
                                            "&:hover": {
                                                border: "none !important",
                                                bgcolor: "#066fcffa",
                                            },
                                            "&.Mui-disabled": {
                                                bgcolor: "#E8EAF0",
                                                color: "#8F8F8F !important",
                                            },
                                        }}
                                    >
                                        Add Sub Account
                                    </Button>

                                    <AddWorkspace
                                        open={dialogs.add}
                                        onClose={handleAddWorkplaceDialogClose}
                                        title="Add Account"
                                        content="This is the dialog content."
                                        onConfirm={() => handleWorkspaceAction('add')}
                                    />
                                </Box>
                            </Box>
                            <Box display="flex" gap="12px">
                                <Box sx={{ width: '100%' }} className='settings-module-listing' display="flex">
                                    <TableContainer component={Paper} sx={{ boxShadow: '0px 4px 12px 0px #2B2B300F', p: { xs: "12px", md: "24px" } }}>
                                        <Table sx={{ minWidth: 650 }} aria-label="simple table">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', fontWeight: "400" }}>Sub Account</TableCell>
                                                    <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', fontWeight: "400" }}>Company Name</TableCell>
                                                    <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', fontWeight: "400" }}>Plan</TableCell>
                                                    <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', width: '20%', fontWeight: "400" }}>Automated Attendees</TableCell>
                                                    <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', width: '20%', fontWeight: "400" }}>Live Attendees</TableCell>
                                                    {/* <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', fontWeight: "400" }}>Amount</TableCell> */}
                                                    <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', fontWeight: "400" }}>Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {loading ? (
                                                    <TableRow sx={{ "& td": { borderBottom: 'none' } }}>
                                                        <TableCell colSpan={5} align="center"><Loader fullHeight minHeight='200px' gifSize={50}/></TableCell>
                                                    </TableRow>
                                                ) : companies.length === 0 ? (
                                                    <EmptyRow />
                                                ) : (
                                                    paginatedCompanies.map((company) => {
                                                        return (
                                                            <WorkspaceRow
                                                                key={company.userId}
                                                                company={company}
                                                                onEdit={() => handleEditWorkplaceDialogOpen(company)}
                                                                onDelete={() => handleDeleteDialogOpen(company)}
                                                                toggleDrawer={toggleDrawer}
                                                            />
                                                        )
                                                    })
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
                
                {!isFreeTrial && !isPausedPlan && (
                    <Box
                        display="flex"
                        justifyContent={{ xs: "center", sm: "space-between" }}
                        alignItems="center"
                        flexWrap="wrap"
                        gap="12px"
                        sx={{ xs: { marginBottom: "24px" }, sm: { marginBottom: "0px" } }}
                        flexDirection={{ xs: "column", sm: "row" }}
                    >
                        <Typography sx={{ marginX: 2 }} color='#2B2B30'>
                            {filteredCompanies.length > 0
                                ? `1-${filteredCompanies.length} of ${filteredCompanies.length} records`
                                : '0 records'
                            }
                        </Typography>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={handleChangePagination}
                            variant="outlined"
                            shape="rounded"
                            sx={{
                                marginX: 2,
                                paddingTop: "0 !important",
                                overflowY: 'unset !important',
                                "& button": {
                                    background: "#FFFFFF",
                                    boxShadow: "0px 0.929px 0.929px 0px rgba(0, 0, 0, 0.20), 0px 1.859px 1.859px 0px rgba(0, 0, 0, 0.14), 0px 0.929px 4.647px 0px rgba(0, 0, 0, 0.12)",
                                    border: "none"
                                },
                                "& .Mui-selected": {
                                    backgroundColor: "#178EFC !important",
                                    color: "#FFFFFF",
                                },
                                "button": {
                                    minWidth: { xs: "20px", sm: "32px" },
                                },
                            }}
                        />
                        <Box width="13%"></Box>
                    </Box>
                )}

                <DeletePopup
                    open={dialogs.delete}
                    onClose={handleCancelClose}
                    onConfirm={handleDeleteWorkspace}
                    title="Delete Workspace"
                    content={`Are you sure you want to delete the workspace "${selectedWorkspace?.companyName}"? This action cannot be undone.`}
                />

                {dialogs.edit && (
                    <EditWorkspace
                        open={dialogs.edit}
                        onClose={handleEditWorkplaceClose}
                        workspaceData={selectedWorkspace || undefined}
                        onConfirm={() => handleWorkspaceAction('edit')}
                    />
                )}
            </Container >

            {/* User Info html sidebar */}
            <Backdrop
                open={open}
                onClick={() => toggleDrawer(false)}
                sx={{ backgroundColor: "rgba(0, 0, 0, 0.2)", zIndex: 1200 }}
            />

            {/* Sidebar Drawer */}
            <Drawer
                anchor="right"
                open={open}
                onClose={() => toggleDrawer(false)}
                transitionDuration={300}
                className='user-info-box'
                sx={{
                    "& .MuiDrawer-paper": {
                        width: 570,
                        maxWidth: "96%",
                        height: "calc(100% - 65px)",
                        transition: "transform 0.3s ease-in-out",
                    },
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        p: 2,
                    }}
                >
                    {/* Sidebar Content */}
                    <Box
                        sx={{
                            width: "100%"
                        }}
                    >
                        <IconButton
                            aria-label="close"
                            onClick={() => toggleDrawer(false)}
                            sx={() => ({
                                position: 'absolute',
                                right: { xs: "17px" },
                                top: { xs: "15px" },

                            })}>
                            <ModalClose></ModalClose>
                        </IconButton>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                                padding: { xs: 1, sm: 2 },
                                borderRadius: 2
                            }}
                        >
                            {/* Profile Image */}
                            <Stack direction="column" alignItems="center">
                                <Avatar
                                    src={selectedWorkspace?.picture}
                                    alt={"User Avatar"}
                                    sx={{ width: { xs: 70, sm: 100 }, height: { xs: 70, sm: 100 } }}
                                />
                            </Stack>

                            <Box sx={{ display: "flex", flexDirection: "column", flex: 1, ml: 2 }}>

                                {/* User Name */}
                                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: 'min(26px, calc(16px + 1.5vw))' }}>
                                    {selectedWorkspace?.name}
                                </Typography>

                                {/* Buttons Section */}
                                <Box mt={2} width="100%"
                                    sx={{
                                        display: "flex",
                                        gap: { xs: "11px", sm: "20px" },
                                        flexWrap: "wrap",
                                        marginTop: "5px"
                                    }}
                                >
                                    <Link
                                        onClick={() => setChangePasswordDialog(true)} 
                                        component="button"
                                        sx={{ color: "#666666", textDecoration: "none", cursor: "pointer", mb: 2, fontSize: "14px", marginBottom: "0", display: "flex", alignItems: "center", gap: "4px" }}>
                                        <PasswordIconBlue />
                                        Change Password
                                    </Link>

                                    <ChangePasswordModal
                                        open={changePasswordDialog}
                                        onClose={() => setChangePasswordDialog(false)}
                                        title="Change Password"
                                        content="Update your password securely."
                                        onConfirm={handleChangePasswordConfirm}
                                        username={selectedWorkspace?.email}
                                    />

                                    <Link
                                        onClick={() => handleDeleteDialogOpen(selectedWorkspace)}
                                        component="button"
                                        sx={{ color: "#666666", textDecoration: "none", cursor: "pointer", mb: 2, fontSize: "14px", marginBottom: "0", display: "flex", alignItems: "center", gap: "4px" }}>
                                        <DeleteIconBlue />
                                        Delete Workspace
                                    </Link>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* tabs */}
                    <Box sx={{ width: "100%", maxWidth: 600, mt: 3, marginTop: "0" }} className='user-info-tabs'>
                        {/* Tabs */}
                        <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth" sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            background: "#ffffff"
                        }}>
                            <Tab label="Profile details" sx={{ textTransform: "capitalize" }} />
                            <Tab label="Subscription" sx={{ textTransform: "capitalize" }} />
                            <Tab label="Domain" sx={{ textTransform: "capitalize" }} />
                        </Tabs>

                        {/* Tab Content */}
                        <Box sx={{ mt: 2, padding: "0 15px 35px", marginTop: "35px" }}>
                            {tabValue === 0 && (
                                <Box>
                                    <Grid container columnSpacing={2} rowSpacing={{ xs: 6, sm: 6 }}>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Box>
                                                <Typography variant="body1" mb="10px" fontWeight="500" color="#333333">First Name</Typography>
                                                <Typography variant="body1" color="textSecondary" fontWeight="normal">
                                                    {selectedWorkspace?.firstName}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Box>
                                                <Typography variant="body1" mb="10px" fontWeight="500" color="#333333">Last Name</Typography>
                                                <Typography variant="body1" color="textSecondary" fontWeight="normal">
                                                    {selectedWorkspace?.lastName}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Box sx={{ wordBreak: "break-all" }}>
                                                <Typography variant="body1" mb="10px" fontWeight="500" color="#333333">Email</Typography>
                                                <Typography variant="body1" color="textSecondary" fontWeight="normal">
                                                    {selectedWorkspace?.email}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Box>
                                                <Typography variant="body1" mb="10px" fontWeight="500" color="#333333">User ID</Typography>
                                                <Typography variant="body1" color="textSecondary" fontWeight="normal">
                                                    {selectedWorkspace?.username}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Box>
                                                <Typography variant="body1" mb="10px" fontWeight="500" color="#333333">Phone Number</Typography>
                                                <Typography variant="body1" color="textSecondary" fontWeight="normal">
                                                    {selectedWorkspace?.phoneNumber}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Box>
                                                <Typography variant="body1" mb="10px" fontWeight="500" color="#333333">Company Name</Typography>
                                                <Typography variant="body1" color="textSecondary" fontWeight="normal">
                                                    {selectedWorkspace?.companyName}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Box sx={{ wordBreak: "break-all" }}>
                                                <Typography variant="body1" mb="10px" fontWeight="500" color="#333333">TimeZone</Typography>
                                                <Typography variant="subtitle2" color="textSecondary" fontWeight="normal">
                                                    {selectedWorkspace?.timezone || "-- --"}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Box>
                                                <Typography variant="body1" mb="10px" fontWeight="500" color="#333333">Job Title</Typography>
                                                <Typography variant="subtitle2" color="textSecondary" fontWeight="normal">
                                                    {selectedWorkspace?.jobTitle || "-- --"}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}
                            {tabValue === 1 && (
                                <Box>
                                    <Box
                                        mt="36px"
                                        pb="19px"
                                        borderRadius="8px"
                                        border="1px solid #E8EAF0"
                                        bgcolor="#FFF"
                                        boxShadow="0px 4px 48px 0px rgba(43, 43, 48, 0.08)"
                                        p="20px"
                                    >
                                        <Typography variant="h6" color="#333333" mb="12px" fontWeight="500">Current Plan</Typography>
                                        <Grid container columnSpacing={2} rowSpacing={{ xs: "12px" }}>
                                            <Grid size={{ xs: 12 }}>
                                                <Box display="flex" alignItems="center" sx={{ borderBottom: '1px solid #E8EAF0', paddingBottom: '12px' }} width="100%" flexWrap="wrap">
                                                    <Typography variant="body2" color="#666666" sx={{ width: "40%", minWidth: "168px" }}>Plan Name</Typography>
                                                    <Typography variant="subtitle1" color="#333333" fontWeight="500" textTransform="capitalize" >
                                                        {formatPlanName(selectedWorkspace?.subscription?.items?.[0]?.itemPriceId || "-- --")}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12 }}>
                                                <Box display="flex" alignItems="center" sx={{ borderBottom: '1px solid #E8EAF0', paddingBottom: '12px' }} width="100%" flexWrap="wrap">
                                                    <Typography variant="body2" color="#666666" sx={{ width: "40%", minWidth: "168px" }}>Attendees/Active Leads</Typography>
                                                    <Typography variant="subtitle1" color="#333333" fontWeight="500">
                                                        {selectedWorkspace?.subscription?.items?.[0]?.quantity || '-- --'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12 }}>
                                                <Box display="flex" alignItems="center" sx={{ borderBottom: '1px solid #E8EAF0', paddingBottom: '12px' }} width="100%" flexWrap="wrap">
                                                    <Typography variant="body2" color="#666666" sx={{ width: "40%", minWidth: "168px" }}>Expires on</Typography>
                                                    <Typography variant="subtitle1" color="#333333" fontWeight="500">
                                                        {selectedWorkspace?.subscription?.nextBillingAt
                                                            ? formatDate(selectedWorkspace.subscription.nextBillingAt)
                                                            : '-- --'
                                                        }
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                             <Grid size={{ xs: 12 }}>
                                                <Box display="flex" alignItems="center" sx={{ borderBottom: '1px solid #E8EAF0', paddingBottom: '12px' }} width="100%" flexWrap="wrap">
                                                    <Typography variant="body2" color="#666666" sx={{ width: "40%", minWidth: "168px" }}>Automated Attendees Usage</Typography>
                                                    <Box flex="1" minWidth="123px">
                                                        {selectedWorkspace?.usage ? (
                                                            <React.Fragment>
                                                                <Typography color="#333333" fontWeight="500" variant="caption">
                                                                    {getEventValue(selectedWorkspace?.usage, 'attendee_registered', 'automated_attendees_count', selectedWorkspace?.subscription?.items?.[0]?.itemPriceId)}/
                                                                    {getEventValue(selectedWorkspace?.usage, 'attendee_registered', 'max_count', selectedWorkspace?.subscription?.items?.[0]?.itemPriceId)} used
                                                                </Typography>
                                                                <BorderLinearProgress 
                                                                    variant="determinate" 
                                                                    value={calculateUsagePercentage(selectedWorkspace?.usage, 'attendee_registered')}
                                                                    sx={{ height: "8px", marginTop: "4px", width: "100%" }} 
                                                                />
                                                            </React.Fragment>
                                                        ) : (
                                                            <React.Fragment>
                                                                <Typography color="#333333" fontWeight="500" variant="caption">0/0 used</Typography>
                                                                <BorderLinearProgress variant="determinate" value={0} sx={{ height: "8px", marginTop: "4px", width: "100%" }} />
                                                            </React.Fragment>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12 }}>
                                                <Box display="flex" alignItems="center" sx={{ borderBottom: '1px solid #E8EAF0', paddingBottom: '12px' }} width="100%" flexWrap="wrap">
                                                    <Typography variant="body2" color="#666666" sx={{ width: "40%", minWidth: "168px" }}>Live Attendees Usage</Typography>
                                                    <Box flex="1" minWidth="123px">
                                                        {selectedWorkspace?.usage ? (
                                                            <React.Fragment>
                                                                <Typography color="#333333" fontWeight="500" variant="caption">
                                                                    {getEventValue(selectedWorkspace?.usage, 'attendee_joined', 'event_count', selectedWorkspace?.subscription?.items?.[0]?.itemPriceId)}/
                                                                    {getEventValue(selectedWorkspace?.usage, 'attendee_joined', 'max_count', selectedWorkspace?.subscription?.items?.[0]?.itemPriceId)} used
                                                                </Typography>
                                                                <BorderLinearProgress 
                                                                    variant="determinate" 
                                                                    value={calculateUsagePercentage(selectedWorkspace?.usage, 'attendee_joined')}
                                                                    sx={{ height: "8px", marginTop: "4px", width: "100%" }} 
                                                                />
                                                            </React.Fragment>
                                                        ) : (
                                                            <React.Fragment>
                                                                <Typography color="#333333" fontWeight="500" variant="caption">0/0 used</Typography>
                                                                <BorderLinearProgress variant="determinate" value={0} sx={{ height: "8px", marginTop: "4px", width: "100%" }} />
                                                            </React.Fragment>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                        <Box mt="24px">
                                            <Button
                                                variant='contained'
                                                size='large'
                                                color='primary'
                                                disableElevation
                                                disabled={selectedWorkspace?.subscription?.status === 'non_renewing'}
                                                sx={{
                                                    textTransform: 'capitalize',
                                                    px: '24px',
                                                    borderRadius: '8px',
                                                    minWidth: "94px",
                                                    '&.Mui-disabled': {
                                                        backgroundColor: '#E8EAF0',
                                                        color: '#A3A3A3 !important'
                                                    }
                                                }}
                                                onClick={handleDialogChangePlan}
                                            >
                                                Change Plan
                                            </Button>
                                            <Button
                                                size='large'
                                                color="error"
                                                variant="text"
                                                disabled={selectedWorkspace?.subscription?.status === 'non_renewing'}
                                                sx={{
                                                    textTransform: 'capitalize',
                                                    px: '24px',
                                                    borderRadius: '8px',
                                                    minWidth: "94px",
                                                    ml: "12px",
                                                    fontWeight: "normal"
                                                }}
                                                onClick={handleDialogCancelAccount}
                                            >
                                                Cancel Plan
                                            </Button>
                                        </Box>
                                    </Box>

                                    {/* modified design */}
                                    <Box
                                        mt="36px"
                                        pb="19px"
                                        borderRadius="8px"
                                        border="1px solid #E8EAF0"
                                        bgcolor="#FFF"
                                        boxShadow="0px 4px 48px 0px rgba(43, 43, 48, 0.08)"
                                        p="20px"
                                    >
                                        <Typography variant="body1" color="#333333" mb="20px">
                                            Subscribed Add-ons ({selectedWorkspace?.subscription?.items?.filter(item => item.itemType === 'addon')?.length || 0})
                                        </Typography>

                                        <Grid container columnSpacing={2} rowSpacing={{ xs: "12px" }}>
                                            {selectedWorkspace?.subscription?.items?.filter(item => item.itemType === 'addon').map((addon, index) => (
                                                <Grid key={index} size={{ xs: 12 }}>
                                                    <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ borderBottom: '1px solid #E8EAF0', paddingBottom: '12px' }} width="100%" flexWrap="wrap" gap="10px">
                                                        <Box display="flex" alignItems="center">
                                                            <Box sx={{ width: "50%", minWidth: "168px" }}>
                                                                <Typography variant="body2" color="#666666" mb="4px" textTransform="capitalize">
                                                                    {formatPlanName(addon.itemPriceId) || "-- --"}
                                                                    {addon.quantity > 1 && ` x ${addon.quantity}`}
                                                                </Typography>
                                                                <Typography variant="caption" color="#333333">
                                                                    {selectedWorkspace?.subscription?.trialStartDate
                                                                        ? formatDate(selectedWorkspace.subscription.trialStartDate)
                                                                        : '-- --'
                                                                    } - {selectedWorkspace?.subscription?.nextBillingAt
                                                                        ? formatDate(selectedWorkspace.subscription.nextBillingAt)
                                                                        : '-- --'
                                                                    }
                                                                </Typography>
                                                            </Box>
                                                            <Typography variant="subtitle1" fontSize="18px" color="#178EFC" fontWeight="500" textTransform="capitalize" ml="24px">
                                                                ${(addon.amount / 100).toFixed(2)}
                                                            </Typography>
                                                        </Box>
                                                        <Button
                                                            size='small'
                                                            color="error"
                                                            variant="text"
                                                            onClick={() => handleCancelAddOn(addon.itemPriceId)}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                        <Box mt="24px">
                                            <Button
                                                variant='contained'
                                                size='large'
                                                color='primary'
                                                disableElevation
                                                sx={{
                                                    textTransform: 'capitalize',
                                                    px: '24px',
                                                    borderRadius: '8px',
                                                    minWidth: "94px",
                                                    '&.Mui-disabled': {
                                                        backgroundColor: '#E8EAF0',
                                                        color: '#A3A3A3 !important'
                                                    }
                                                }}
                                                onClick={handleDialogBuyAddOns}
                                            >
                                                Buy Add-Ons
                                            </Button>
                                            <BuyAddOns
                                                open={dialogBuyAddOns}
                                                onClose={handleDialogBuyAddOnsClose}
                                                onConfirm={handleDialogBuyAddOnsConfirm}
                                                subscriptionId={selectedWorkspace?.subscription?.id}
                                                subscribedAddons={selectedWorkspace?.subscription?.items?.filter(item => item.itemType === 'addon').map(addon => addon.itemPriceId) || []}
                                            />
                                        </Box>
                                    </Box>
                                    {/* modified design */}

                                </Box>
                            )}
                            {tabValue === 2 && (
                                <Box>
                                    <Box
                                        mt="36px"
                                        pb="19px"
                                        borderRadius="8px"
                                        border="1px solid #E8EAF0"
                                        bgcolor="#FFF"
                                        boxShadow="0px 4px 48px 0px rgba(43, 43, 48, 0.08)"
                                        p="20px"
                                    >
                                        <Typography variant="h6" color="#333333" mb="12px" fontWeight="500">Domain Details</Typography>
                                        <Grid container columnSpacing={2} rowSpacing={{ xs: "12px" }}>
                                            <Grid size={{ xs: 12 }}>
                                                <Box display="flex" alignItems="center" sx={{ borderBottom: '1px solid #E8EAF0', paddingBottom: '12px' }} width="100%" flexWrap="wrap">
                                                    <Typography variant="body2" color="#666666" sx={{ width: "30%", minWidth: "130px" }}>Sub Domain</Typography>
                                                    <Typography variant="body1" color="#333333" sx={{ wordBreak: "break-all" }} flex={1}>
                                                        https://{selectedWorkspace?.domains?.easywebinar}.easywebinar.live
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 12 }}>
                                                <Box display="flex" alignItems="center" sx={{ borderBottom: '1px solid #E8EAF0', paddingBottom: '12px' }} width="100%" flexWrap="wrap">
                                                    <Typography variant="body2" color="#666666" sx={{ width: "30%", minWidth: "130px" }}>Custom Domain</Typography>
                                                    <Typography variant="body1" color="#333333" sx={{ wordBreak: "break-all", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} flex={1}>
                                                        {selectedWorkspace?.customDomain || "-- --"}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box >
            </Drawer >

            {showPricing && (
                <Dialog
                    open={showPricing}
                    onClose={() => !isChangingPlan && setShowPricing(false)}
                    maxWidth="xl"
                    fullWidth
                    PaperProps={{
                        sx: {
                            maxWidth: '800px',
                            height: '95%',
                            m: 2,
                            borderRadius: 2,
                            position: 'relative'
                        }
                    }}
                    sx={{
                        '& .MuiDialog-paper': {
                            width: '100%',
                            maxWidth: '800px',
                            height: 'fit-content',
                            borderRadius: '16px',
                            border: '1px solid #E8E8EA'
                        }
                    }}
                >
                    {isChangingPlan && (
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bgcolor: 'rgba(255,255,255,0.95)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 3,
                            backdropFilter: 'blur(4px)'
                        }}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 2
                            }}>
                                <CircularProgress size={40} />
                                <Typography variant="h6">Updating plan...</Typography>
                            </Box>
                        </Box>
                    )}
                    <DialogContent sx={{
                        p: { xs: "16px", sm: "32px" },
                        height: '100%',
                        overflow: 'auto',
                        position: 'relative'
                    }}>
                        <IconButton
                            onClick={() => !isChangingPlan && setShowPricing(false)}
                            sx={{
                                position: 'absolute',
                                right: 16,
                                top: 16,
                                zIndex: 1,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <ModalClose />
                        </IconButton>
                        <Box className="add-workspace-modal-pricing-section">
                            {/* <PricingSection
                                currentPlanId={selectedWorkspace?.subscription?.items?.[0]?.itemPriceId || ''}
                                currentAttendees={selectedWorkspace?.subscription?.items?.[0]?.quantity || 0}
                                onPlanSelect={handlePlanChanged}
                            /> */}
                            <PricingSectionV2
                                currentPlanId={selectedWorkspace?.subscription?.items?.[0]?.itemPriceId || ''}
                                currentAttendees={selectedWorkspace?.subscription?.items?.[0]?.quantity || 0}
                                onPlanSelect={handlePlanChanged}
                                hideTabs={true}
                            />
                        </Box>
                    </DialogContent>
                </Dialog>
            )}

            {/* Add CancelAccountModal component */}
            {selectedWorkspace?.subscription?.id && (
                <CancelAccountModal
                    open={dialogCancelAccount}
                    onClose={handleDialogCancelAccountClose}
                    subscriptionId={selectedWorkspace.subscription.id}
                />
            )}

            {/* Add DeletePopup for cancel add-on confirmation */}
            <DeletePopup
                open={dialogCancelAddOn}
                onClose={handleCancelAddOnClose}
                onConfirm={handleCancelAddOnConfirm}
                title="Cancel Subscription"
                content="Are you sure you want to cancel this Add-on Subscription?"
            />
        </>
    );
};

// Helper components for table rows
const LoadingRow = () => (
    <TableRow>
        <TableCell colSpan={7} sx={{ border: 'none' }}>
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress />
            </Box>
        </TableCell>
    </TableRow>
);

const EmptyRow = () => (
    <TableRow>
        <TableCell colSpan={7} sx={{ border: 'none' }}>
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <Typography color="textSecondary">No sub accounts found</Typography>
            </Box>
        </TableCell>
    </TableRow>
);

const WorkspaceRow = ({ company, onEdit, onDelete, toggleDrawer }: { company: CompanyData, onEdit: () => void, onDelete: () => void, toggleDrawer: (status: boolean, company: CompanyData) => void }) => {
    return (
        <TableRow
            key={company.userId}
            sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer', '&:hover': { backgroundColor: "#178efc0a" } }}
            onClick={() => toggleDrawer(true, company)}
        >
            <TableCell align="left" component="th" scope="row" sx={{ color: '#231F1F', fontSize: '18px', borderBottom: '1px solid #E8EAF0' }}>
                <Box display="flex" alignItems="center" gap="10px">
                    <Avatar alt={company?.name} src={company?.picture} sx={{ color: "#333333", bgcolor: "#5aaffd42", fontSize: "16px" }}>
                        {!company?.picture && `${company?.firstName[0]}`}
                    </Avatar>
                    <Box>
                        <Typography fontSize="16px">
                            {company.name}
                        </Typography>
                        <Typography fontSize="14px" color='#666666'>{company.email}</Typography>
                    </Box>
                </Box>
            </TableCell>
            <TableCell align="left" sx={{ color: '#333333', fontSize: '16px', borderBottom: '1px solid #E8EAF0', minWidth: "149px", maxWidth: "153px" }}>
                <Typography noWrap={true} title={company.companyName}>{company.companyName}</Typography>
            </TableCell>
            <TableCell align="left" sx={{ color: '#333333', fontSize: '16px', borderBottom: '1px solid #E8EAF0', minWidth: '147px' }}>
                <Typography variant="body1">
                    {formatPlanName(company.subscription?.items?.[0]?.itemPriceId || "-- --") || "-- --"}
                </Typography>
            </TableCell>
            <TableCell align="left" sx={{ color: '#333333', fontSize: '18px', borderBottom: '1px solid #E8EAF0', minWidth: '153px' }}>
                <Box width="100%">
                    <Typography color="#2B2B30" fontSize="14px">
                        {getEventValue(company.usage, 'attendee_registered', 'automated_attendees_count',company.subscription?.items?.[0]?.itemPriceId)}/
                        {getEventValue(company.usage, 'attendee_registered', 'max_count',company.subscription?.items?.[0]?.itemPriceId)}
                    </Typography>
                    <BorderLinearProgress 
                        variant="determinate" 
                        value={calculateUsagePercentage(company.usage, 'attendee_registered')} 
                        sx={{ marginBottom: "8px", height: "4px", marginTop: "4px" }} 
                    />
                </Box>
            </TableCell>
            <TableCell align="left" sx={{ color: '#333333', fontSize: '18px', borderBottom: '1px solid #E8EAF0', minWidth: '159px' }}>
                <Box width="100%">
                    <Typography color="#2B2B30" fontSize="14px">
                        {getEventValue(company.usage, 'attendee_joined', 'event_count',company.subscription?.items?.[0]?.itemPriceId)}/
                        {getEventValue(company.usage, 'attendee_joined', 'max_count',company.subscription?.items?.[0]?.itemPriceId)}
                    </Typography>
                    <BorderLinearProgress 
                        variant="determinate" 
                        value={calculateUsagePercentage(company.usage, 'attendee_joined')} 
                        sx={{ marginBottom: "8px", height: "4px", marginTop: "4px" }} 
                    />
                </Box>
            </TableCell>
            {/* <TableCell align="left" sx={{ color: '#2B2B30', fontSize: '14px', borderBottom: '1px solid #E8EAF0' }}>
                <Typography variant="body2">
                    {company.subscription?.items?.[0]?.amount ?
                        `$${(company.subscription.items[0].amount / 100).toFixed(2)}` :
                        "-- --"}
                </Typography>
            </TableCell> */}
            <TableCell align="left" sx={{ cursor: 'pointer' }}><Box display="flex" gap="10px">
                <Box width="28px" height="28px" borderRadius="4px" border="1px solid #E8EAF0" padding="2px" sx={{ ":hover": { borderColor: "#666666" } }} display="flex">
                    <Tooltip title="Edit" arrow>
                        <Box
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit();
                            }}
                            sx={{ cursor: 'pointer' }}
                        >
                            <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/edit-btn.svg" alt="edit-btn" />
                        </Box>
                    </Tooltip>
                </Box>
                <Box width="28px" height="28px" borderRadius="4px" border="1px solid #E8EAF0" padding="2px" display="flex" sx={{ ":hover": { borderColor: "#666666" } }} onClick={(e) => { e.stopPropagation(); onDelete(); }}>
                    <Tooltip
                        title="Delete"
                        arrow
                        slotProps={{
                            tooltip: {
                                sx: {
                                    backgroundColor: '#000',
                                    color: '#fff',
                                    '& .MuiTooltip-arrow': {
                                        color: '#000',
                                    },
                                    height: '36px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '14px',
                                    padding: '0 16px',
                                }
                            },
                        }}
                    >
                        <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/delete-btn.svg" alt="delete-outline-btn" />
                    </Tooltip>
                </Box>
            </Box></TableCell>
        </TableRow>
    );
};

export default Workspaces;
